// src/context/NotificationContext.jsx
//
// Bridges the framework-agnostic errorBus store into React and renders the
// on-screen NotificationCenter. Components call useNotifications() to push
// friendly messages or report errors; everything else (axios, window handlers)
// talks to the bus directly.
import React, { createContext, useCallback, useContext, useSyncExternalStore } from 'react';
import {
    subscribe,
    getSnapshot,
    reportError as busReportError,
    notify as busNotify,
    dismissNotification,
    clearNotifications,
} from '../notifications/errorBus';
import NotificationCenter from '../components/notifications/NotificationCenter';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    const notifications = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

    const dismiss = useCallback((id) => dismissNotification(id), []);
    const clear = useCallback(() => clearNotifications(), []);
    const reportError = useCallback((error, opts) => busReportError(error, opts), []);
    const notify = useCallback((opts) => busNotify(opts), []);

    const value = { notifications, reportError, notify, dismiss, clear };

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <NotificationCenter notifications={notifications} onDismiss={dismiss} onClear={clear} />
        </NotificationContext.Provider>
    );
};

/**
 * Access the notification helpers. Safe to call outside the provider — it falls
 * back to the global bus so a stray call never throws.
 */
export const useNotifications = () => {
    const ctx = useContext(NotificationContext);
    if (ctx) return ctx;
    return {
        notifications: [],
        reportError: busReportError,
        notify: busNotify,
        dismiss: dismissNotification,
        clear: clearNotifications,
    };
};

export default NotificationContext;
